import { useState, useCallback, useRef, useEffect } from 'react';

import { submitCalculation, getJobStatus, getJobResult, isNoSolutionMessage, PackingErrorCode } from '../services/packing-service';
import type { PackingError, JobStatusResponse, JobResultResponse } from '../services/packing-service';
import type { PackingRequest } from '../utils/data-transformer';

const DEFAULT_POLLING_INTERVAL_MS = 2000;
const DEFAULT_MAX_POLLING_TIME_MS = 300000;

type UsePackingCalculationOptions = {
  pollingIntervalMs?: number;
  maxPollingTimeMs?: number;
  onComplete?: (result: JobResultResponse) => void;
  onError?: (error: PackingError) => void;
};

type UsePackingCalculationReturn = {
  isCalculating: boolean;
  jobId: string | null;
  status: JobStatusResponse | null;
  result: JobResultResponse | null;
  error: PackingError | null;
  startCalculation: (request: PackingRequest) => Promise<void>;
  resumePolling: (activeJobId: string, startTime: number) => void;
  reset: () => void;
};

export const usePackingCalculation = (options: UsePackingCalculationOptions = {}): UsePackingCalculationReturn => {
  const { pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS, maxPollingTimeMs = DEFAULT_MAX_POLLING_TIME_MS, onComplete, onError } = options;

  const [isCalculating, setIsCalculating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatusResponse | null>(null);
  const [result, setResult] = useState<JobResultResponse | null>(null);
  const [error, setError] = useState<PackingError | null>(null);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const isPollingInFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const handleError = useCallback(
    (err: PackingError) => {
      if (!isMountedRef.current) return;

      const rawErr = err as unknown as Error;
      if (rawErr.name === 'CanceledError' || rawErr.name === 'AbortError') return;

      stopPolling();
      setError(err);
      setIsCalculating(false);

      onError?.(err);
    },
    [stopPolling, onError],
  );

  const handleComplete = useCallback(
    (packingResult: JobResultResponse) => {
      if (!isMountedRef.current) return;

      stopPolling();
      setResult(packingResult);
      setIsCalculating(false);

      onComplete?.(packingResult);
    },
    [stopPolling, onComplete],
  );

  const pollStatus = useCallback(
    async (currentJobId: string) => {
      if (!isMountedRef.current || isPollingInFlightRef.current) return;

      const elapsedTimeMs = Date.now() - (pollingStartTimeRef.current ?? 0);
      if (elapsedTimeMs > maxPollingTimeMs) {
        handleError({
          code: PackingErrorCode.TIMEOUT,
          message: 'Calculation timed out. Please try again.',
          details: { elapsedTimeMs, maxPollingTimeMs },
          retryable: true,
        });
        return;
      }

      isPollingInFlightRef.current = true;
      try {
        const signal = abortControllerRef.current?.signal;
        const statusData = await getJobStatus(currentJobId, { signal });

        if (!isMountedRef.current) return;

        setStatus(statusData);

        if (statusData.status === 'completed') {
          const resultData = await getJobResult(currentJobId, { signal });
          handleComplete(resultData);
        } else if (statusData.status === 'failed') {
          const failedMessage = statusData.error ?? 'Calculation failed';
          handleError({
            code: isNoSolutionMessage(failedMessage) ? PackingErrorCode.NO_SOLUTION_FOUND : PackingErrorCode.CALCULATION_FAILED,
            message: failedMessage,
            details: statusData as unknown as Record<string, unknown>,
            retryable: true,
          });
        }
      } catch (err) {
        handleError(err as PackingError);
      } finally {
        isPollingInFlightRef.current = false;
      }
    },
    [maxPollingTimeMs, handleError, handleComplete],
  );

  const pollStatusRef = useRef(pollStatus);
  pollStatusRef.current = pollStatus;

  const startPolling = useCallback(
    (currentJobId: string) => {
      stopPolling();
      abortControllerRef.current = new AbortController();
      pollingStartTimeRef.current = Date.now();

      pollStatusRef.current(currentJobId);

      pollingIntervalRef.current = setInterval(() => {
        pollStatusRef.current(currentJobId);
      }, pollingIntervalMs);
    },
    [stopPolling, pollingIntervalMs],
  );

  const startCalculation = useCallback(
    async (request: PackingRequest) => {
      stopPolling();
      abortControllerRef.current = new AbortController();

      setIsCalculating(true);
      setError(null);
      setResult(null);
      setJobId(null);
      setStatus(null);
      try {
        const response = await submitCalculation(request, { signal: abortControllerRef.current.signal });

        if (!isMountedRef.current) return;

        setJobId(response.jobId);
        setStatus({ jobId: response.jobId, status: 'queued', progress: 0 });
        startPolling(response.jobId);
      } catch (err) {
        handleError(err as PackingError);
      }
    },
    [stopPolling, startPolling, handleError],
  );

  const resumePolling = useCallback(
    (activeJobId: string, startTime: number) => {
      stopPolling();
      abortControllerRef.current = new AbortController();

      setIsCalculating(true);
      setError(null);
      setResult(null);
      setJobId(activeJobId);
      setStatus({ jobId: activeJobId, status: 'processing' });
      pollingStartTimeRef.current = startTime;

      pollStatusRef.current(activeJobId);
      pollingIntervalRef.current = setInterval(() => {
        pollStatusRef.current(activeJobId);
      }, pollingIntervalMs);
    },
    [stopPolling, pollingIntervalMs],
  );

  const reset = useCallback(() => {
    stopPolling();
    setIsCalculating(false);
    setJobId(null);
    setStatus(null);
    setResult(null);
    setError(null);
  }, [stopPolling]);

  return {
    isCalculating,
    jobId,
    status,
    result,
    error,
    startCalculation,
    resumePolling,
    reset,
  };
};
